import {
  formatMatchDateTime,
  formatMatchDuration,
  formatMatchTypeLabel,
  formatSidePlayerLabel,
  groupParticipantsByTeam,
  participantAvatarUrl,
  participantDisplayName,
} from "./matchHistoryPresentation";
import { buildMatchInsightStats } from "./buildMatchInsightStats";
import { buildMatchIntelligence } from "./buildMatchIntelligence";
import { resolveSportModule } from "../../sports/registry";
import { normalizeSportId, DEFAULT_SPORT } from "../sport/sportTypes";

function collapseSidePlayers(players, singles) {
  if (singles) return (players || []).slice(0, 1);
  const seen = new Set();
  return (players || []).filter((player) => {
    const key = String(player?.playerId || player?.displayName || "")
      .trim()
      .toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * @param {Object} input
 * @param {import('./matchHistoryTypes').MatchRecord} input.record
 * @param {import('./matchHistoryTypes').MatchParticipant[]} input.participants
 * @param {number} input.completedAtMs
 * @param {Record<string, string>} [input.nameByPlayerId]
 * @param {Record<string, string | null>} [input.avatarByPlayerId]
 * @param {string | null} [input.viewerPlayerId] — when set, story is viewer-centric
 * @param {Array<object>} [input.careerImpact] — precomputed career impact cards
 */
export function buildMatchDetailViewModel(input) {
  const {
    record,
    participants,
    completedAtMs,
    nameByPlayerId = {},
    avatarByPlayerId = {},
    viewerPlayerId = null,
    careerImpact = [],
  } = input;
  const grouped = groupParticipantsByTeam(participants, nameByPlayerId);
  const singles =
    record.matchType !== "doubles" && record.matchType !== "mixed";
  const teamA = collapseSidePlayers(grouped.teamA, singles);
  const teamB = collapseSidePlayers(grouped.teamB, singles);
  const winnerSide = record.winnerSide;
  const winnerTeam = winnerSide === "B" ? teamB : teamA;
  const teamALabel = formatSidePlayerLabel(
    teamA.map((p) => p.displayName),
    { singles }
  ) || "Team A";
  const teamBLabel = formatSidePlayerLabel(
    teamB.map((p) => p.displayName),
    { singles }
  ) || "Team B";
  const winnerLabel = formatSidePlayerLabel(
    winnerTeam.map((p) => p.displayName),
    { singles }
  ) || (winnerSide === "A" ? "Team A" : "Team B");
  const sportId = normalizeSportId(record.sportId || DEFAULT_SPORT);
  const sportMod = resolveSportModule(sportId);
  const sets = record.sets || [];
  const insights = buildMatchInsightStats({
    sets,
    winnerSide,
    durationSec: record.durationSec ?? null,
    teamALabel,
    teamBLabel,
  });

  /** @type {'win'|'loss'|null} */
  let resultForViewer = null;
  let viewerSide = null;
  if (viewerPlayerId) {
    const mine = (participants || []).find((p) => p.playerId === viewerPlayerId);
    if (mine?.team === "A" || mine?.team === "B") {
      viewerSide = mine.team;
      if (winnerSide === "A" || winnerSide === "B") {
        resultForViewer = mine.team === winnerSide ? "win" : "loss";
      }
    }
  }

  const statsProvider =
    sportMod?.createStatisticsProvider?.() || sportMod?.createStatsProvider?.();
  const sportIntelligence =
    typeof statsProvider?.projectMatchIntelligence === "function"
      ? statsProvider.projectMatchIntelligence(record, insights) || {}
      : {};

  const intelligence = buildMatchIntelligence({
    sets,
    winnerSide,
    durationSec: record.durationSec ?? null,
    teamALabel,
    teamBLabel,
    sportId,
    resultForViewer,
    sportIntelligence,
    careerImpact,
    rallySummary: record.rallySummary || null,
  });

  return {
    matchId: record.matchId,
    sportId,
    sportLabel: sportMod?.displayName || sportId,
    matchType: record.matchType,
    matchTypeLabel: formatMatchTypeLabel(record.matchType),
    completedAt: completedAtMs,
    completedAtLabel: formatMatchDateTime(completedAtMs),
    durationSec: record.durationSec ?? null,
    durationLabel: formatMatchDuration(record.durationSec),
    winnerSide,
    winnerLabel,
    teamALabel,
    teamBLabel,
    resultSummary: record.result || "",
    scoreSummary: sets.map((s) => `${s.scoreA ?? 0}-${s.scoreB ?? 0}`).join(", "),
    sets,
    teamA,
    teamB,
    viewerSide,
    resultForViewer,
    competitionId: record.competitionId ?? null,
    clubId: record.clubId ?? null,
    venueId: record.venueId ?? null,
    venueLabel: record.venueId ? String(record.venueId) : null,
    courtLabel: record.courtLabel || record.courtId || null,
    arenaLabel: record.arenaName || record.sessionRoomLabel || null,
    sessionRoomId: record.sessionRoomId ?? null,
    participants: (participants || []).map((p) => ({
      ...p,
      displayName: participantDisplayName(p, nameByPlayerId),
      avatarUrl: participantAvatarUrl(p, avatarByPlayerId),
    })),
    insights: intelligence.insights,
    intelligence,
    /** Future-ready officials (umpire / service judge) — null until logged */
    officials: {
      umpire: record.umpireName || null,
      serviceJudge: record.serviceJudgeName || null,
    },
    details: {
      players: [...teamA, ...teamB].map((p) => p.displayName).filter(Boolean),
      timeRangeLabel: formatMatchDateTime(completedAtMs),
      durationLabel: formatMatchDuration(record.durationSec),
      tournament: record.competitionId ? String(record.competitionId) : null,
      scoreSettings: record.scoreSettings || null,
    },
    extensionSlots: {
      headToHead: null,
      ratingChange: null,
      shareSpectatorLink: null,
      /** AI-ready cards — deterministic until AI phase */
      aiInsights: intelligence.aiSlots,
      shareStoryCard: intelligence.shareReady
        ? {
            title: intelligence.story?.title,
            summary: intelligence.story?.summary,
            dna: intelligence.dna?.label,
            score: sets.map((s) => `${s.scoreA ?? 0}-${s.scoreB ?? 0}`).join(", "),
          }
        : null,
    },
  };
}
